import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createBuildingType, deleteBuildingType, fetchBuildingTypes, invalidateBuildingTypes, updateBuildingType } from "../api/projectStructureApi";
import { childrenOf, indexTypes } from "../structure/buildingTypes";
import { confirmDialog } from "../utils/confirmDialog";

const MAX_LEVEL = 5;
const row = { display: "flex", alignItems: "center", gap: 6, padding: "3px 4px", borderBottom: "1px solid rgba(148,163,184,0.15)" };
const muted = { opacity: 0.6, fontSize: 11, fontVariantNumeric: "tabular-nums" };
const input = { flex: 1, minWidth: 0 };

const byName = (a, b) => a.name_ka.localeCompare(b.name_ka, "ka");

/** Building classification (the types the new-project window offers): browse and, for superusers, edit. */
export default function BuildingTypesPanel({ token, isSuperuser }) {
  const [index, setIndex] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // { code, name_ka, name_en } | { parent, name_ka, name_en, isNew: true }
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    invalidateBuildingTypes();
    const data = await fetchBuildingTypes(token);
    setIndex(indexTypes(data.items || []));
  }, [token]);

  useEffect(() => {
    load().catch((err) => setMessage({ kind: "error", text: err.message }));
  }, [load]);

  const q = query.trim().toLowerCase();
  const visible = useMemo(() => {
    if (!index || !q) return null;
    const keep = new Set();
    index.items.forEach((t) => {
      if (`${t.name_ka} ${t.name_en || ""} ${t.code}`.toLowerCase().includes(q)) {
        let x = t;
        while (x) {
          keep.add(x.code);
          x = index.byCode[x.parent];
        }
      }
    });
    return keep;
  }, [index, q]);

  const act = async (fn, okText) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      await load();
      setEditing(null);
      if (okText) setMessage({ kind: "ok", text: okText });
    } catch (err) {
      setMessage({ kind: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    const body = { name_ka: editing.name_ka.trim(), name_en: (editing.name_en || "").trim() };
    if (!body.name_ka) return setMessage({ kind: "error", text: "ქართული დასახელება სავალდებულოა" });
    if (editing.isNew) {
      return act(async () => {
        const t = await createBuildingType(token, { parent: editing.parent, ...body });
        if (editing.parent) setOpen((s) => new Set(s).add(editing.parent));
        setMessage({ kind: "ok", text: `დაემატა ${t.code} ${t.name_ka}` });
      });
    }
    return act(() => updateBuildingType(token, editing.code, body), "შეინახა");
  };

  const remove = async (t) => {
    if (!(await confirmDialog(`წაიშალოს „${t.name_ka}“ (${t.code})?`, { confirmLabel: "წაშლა", cancelLabel: "გაუქმება" }))) return;
    act(() => deleteBuildingType(token, t.code), "წაიშალა");
  };

  const editor = (
    <div style={{ ...row, background: "rgba(6,182,212,0.12)" }}>
      <input style={input} autoFocus placeholder="დასახელება (ქართ.)" value={editing?.name_ka || ""} onChange={(e) => setEditing({ ...editing, name_ka: e.target.value })} onKeyDown={(e) => e.key === "Enter" && save()} />
      <input style={input} placeholder="English" value={editing?.name_en || ""} onChange={(e) => setEditing({ ...editing, name_en: e.target.value })} onKeyDown={(e) => e.key === "Enter" && save()} />
      <button type="button" className="primary" disabled={busy} onClick={save}>
        ✓
      </button>
      <button type="button" className="boq-action-btn" onClick={() => setEditing(null)}>
        ✕
      </button>
    </div>
  );

  const renderLevel = (parentCode, depth) =>
    childrenOf(index, parentCode)
      .filter((t) => !visible || visible.has(t.code))
      .sort(byName)
      .map((t) => {
        const kids = childrenOf(index, t.code);
        const expanded = !!visible || open.has(t.code);
        return (
          <React.Fragment key={t.code}>
            {editing?.code === t.code ? (
              <div style={{ paddingLeft: depth * 16 }}>{editor}</div>
            ) : (
              <div style={{ ...row, paddingLeft: 4 + depth * 16 }}>
                <button
                  type="button"
                  className="link"
                  style={{ width: 18, visibility: kids.length ? "visible" : "hidden" }}
                  onClick={() => setOpen((s) => {
                    const n = new Set(s);
                    if (n.has(t.code)) n.delete(t.code);
                    else n.add(t.code);
                    return n;
                  })}
                >
                  {expanded ? "▾" : "▸"}
                </button>
                <span style={{ flex: 1, minWidth: 0 }}>
                  {t.name_ka}
                  {t.name_en && <span style={{ ...muted, marginLeft: 6 }}>{t.name_en}</span>}
                </span>
                {kids.length > 0 && <span style={muted}>{kids.length}</span>}
                <span style={muted}>{t.code}</span>
                {isSuperuser && (
                  <>
                    <button type="button" className="boq-action-btn" title="ქვე-ტიპი" disabled={t.level >= MAX_LEVEL} onClick={() => setEditing({ parent: t.code, name_ka: "", name_en: "", isNew: true })}>
                      +
                    </button>
                    <button type="button" className="boq-action-btn" title="გადარქმევა" onClick={() => setEditing({ code: t.code, name_ka: t.name_ka, name_en: t.name_en || "" })}>
                      ✎
                    </button>
                    <button type="button" className="boq-action-btn" title="წაშლა" disabled={kids.length > 0} onClick={() => remove(t)}>
                      🗑
                    </button>
                  </>
                )}
              </div>
            )}
            {editing?.isNew && editing.parent === t.code && <div style={{ paddingLeft: (depth + 1) * 16 }}>{editor}</div>}
            {expanded && kids.length > 0 && renderLevel(t.code, depth + 1)}
          </React.Fragment>
        );
      });

  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 12, opacity: 0.8 }}>
        შენობების კლასიფიკატორი — ახალი პროექტის ფანჯარა ამ სიიდან სთავაზობს შენობის ტიპს. კოდს სერვერი ანიჭებს მშობლის მიხედვით.
        {!isSuperuser && " რედაქტირება მხოლოდ ადმინისტრატორს შეუძლია."}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <input style={input} placeholder="ძებნა: დასახელება ან კოდი" value={query} onChange={(e) => setQuery(e.target.value)} />
        {isSuperuser && (
          <button type="button" className="boq-action-btn" onClick={() => setEditing({ parent: null, name_ka: "", name_en: "", isNew: true })}>
            + ზედა დონის ტიპი
          </button>
        )}
      </div>
      {message && <div className={message.kind === "ok" ? "msg-success" : "msg-error"}>{message.text}</div>}
      {editing?.isNew && !editing.parent && editor}
      <div style={{ maxHeight: "60vh", overflow: "auto" }}>
        {!index ? <div>იტვირთება…</div> : renderLevel("", 0)}
        {index && visible && !visible.size && <div style={{ opacity: 0.7 }}>„{query}“ — ვერაფერი მოიძებნა</div>}
      </div>
    </div>
  );
}
